import { useState, type FormEvent } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  useActivationIa,
  useQuotaApprenantsFormation,
  useReglerCompteIa,
  type CibleActivation,
  type LigneApprenantIa,
  type LigneCompteIa,
  type LigneFormationIa,
  type ReglageIa,
} from '../../lib/useUsageIa';

const MOTIF_MIN = 5;
const MOTIF_MAX = 500;

export const CLASSE_CHAMP_IA =
  'w-full min-h-[44px] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/**
 * Désactiver ou réactiver l'IA d'un formateur ou d'une formation. Le motif est
 * exigé : il est dit à la personne concernée, et gardé au journal.
 */
export function ModaleActivationIa({
  cible,
  id,
  nom,
  desactiver,
  onFermer,
}: {
  cible: CibleActivation;
  id: string;
  nom: string;
  desactiver: boolean;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const activation = useActivationIa();
  const [motif, setMotif] = useState('');

  const confirmer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    activation.mutate(
      { cible, id, desactiver, motif: motif.trim() },
      {
        onSuccess: (resultat) => {
          toast(resultat.message);
          onFermer();
        },
      },
    );
  };

  const portee =
    cible === 'formateurs'
      ? 'Sa génération de contenu et son tuteur IA'
      : 'La génération de contenu pour cette formation, le tuteur de ses apprenants et la correction proposée de leurs tests';

  return (
    <Modale
      titre={`${desactiver ? 'Désactiver' : 'Réactiver'} l’IA · ${nom}`}
      sousTitre={desactiver ? 'Effet dès la prochaine requête, sans attendre' : 'Les fonctions IA rouvrent aussitôt'}
      ouverte
      onFermer={onFermer}
    >
      <form onSubmit={confirmer} className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-navy">
          {portee} {desactiver ? 'seront refusés' : 'redeviennent disponibles'}
          {cible === 'formations' && desactiver ? ', quel que soit le formateur.' : '.'}
        </p>
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Motif {desactiver ? 'de la désactivation' : 'de la réactivation'}
          </span>
          <textarea
            required
            rows={3}
            maxLength={MOTIF_MAX}
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            placeholder={
              desactiver ? 'Ce qui justifie la coupure, dit à la personne concernée' : 'Ce qui permet de rouvrir l’IA'
            }
            className={`${CLASSE_CHAMP_IA} resize-y`}
          />
        </label>
        <p className="text-xs text-codialis-gray">
          {desactiver
            ? 'Le motif s’affiche à qui se voit refuser une requête. Le geste est tracé au journal d’audit.'
            : 'Le geste est tracé au journal d’audit.'}
        </p>
        {activation.error && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageErreur(activation.error, 'Le geste n’a pas pu être enregistré.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={activation.isPending || motif.trim().length < MOTIF_MIN}>
            {activation.isPending ? 'Enregistrement…' : desactiver ? 'Désactiver l’IA' : 'Réactiver l’IA'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

function versSaisie(valeur: number | null): string {
  return valeur === null ? '' : String(valeur);
}

function lireSaisie(saisie: string): number | null {
  return saisie.trim() === '' ? null : Number(saisie);
}

function ChampNombre({
  libelle,
  valeur,
  onChange,
  reglage,
  placeholder,
  aide,
}: {
  libelle: string;
  valeur: string;
  onChange: (valeur: string) => void;
  reglage: ReglageIa;
  placeholder: string;
  aide: string;
}): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">{libelle}</span>
      <input
        type="number"
        inputMode="numeric"
        min={reglage.min}
        max={reglage.max}
        step={1}
        value={valeur}
        onChange={(evenement) => onChange(evenement.target.value)}
        placeholder={placeholder}
        className={CLASSE_CHAMP_IA}
      />
      <span className="mt-1 block text-[11px] text-codialis-gray">{aide}</span>
    </label>
  );
}

/**
 * Le quota IA d'un compte — vide, l'IA lui est fermée — et, pour un
 * formateur, son seuil d'alerte propre — vide, celui de l'organisme.
 */
export function ModaleReglageCompte({
  ligne,
  formateur,
  reglage,
  onFermer,
}: {
  ligne: LigneCompteIa;
  /** Seuil propre du formateur ; absent pour un apprenant, qui n'a pas de seuil d'alerte. */
  formateur?: { seuilPropre: number | null };
  reglage: ReglageIa;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const regler = useReglerCompteIa();
  const [quota, setQuota] = useState(versSaisie(ligne.quota));
  const [seuil, setSeuil] = useState(versSaisie(formateur?.seuilPropre ?? null));

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    regler.mutate(
      {
        utilisateurId: ligne.utilisateurId,
        quotaJour: lireSaisie(quota),
        seuilAlerteJour: formateur ? lireSaisie(seuil) : null,
      },
      {
        onSuccess: (resultat) => {
          toast(resultat.message);
          onFermer();
        },
      },
    );
  };

  return (
    <Modale
      titre={`Quota IA · ${ligne.prenom} ${ligne.nom}`}
      sousTitre="Requêtes de génération et de tuteur par jour · effet dès la prochaine requête"
      ouverte
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={enregistrer} className="space-y-4 p-4 sm:p-6">
        <div className={`grid grid-cols-1 gap-4 ${formateur ? 'sm:grid-cols-2' : ''}`}>
          <ChampNombre
            libelle="Quota"
            valeur={quota}
            onChange={setQuota}
            reglage={reglage}
            placeholder="Vide : IA fermée"
            aide="Atteint, les requêtes sont refusées jusqu’au lendemain. Vide, l’IA est fermée au compte."
          />
          {formateur && (
            <ChampNombre
              libelle="Seuil d’alerte"
              valeur={seuil}
              onChange={setSeuil}
              reglage={reglage}
              placeholder={`Organisme : ${reglage.seuilAlerteJour}`}
              aide="Au-delà, la direction est alertée. Vide, celui de l’organisme."
            />
          )}
        </div>
        {regler.error && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageErreur(regler.error, 'Le quota n’a pas pu être enregistré.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={regler.isPending}>
            {regler.isPending ? 'Enregistrement…' : quota.trim() === '' ? 'Fermer l’IA' : 'Enregistrer'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

/**
 * D'un geste, le même quota pour chaque apprenant en formation d'une
 * formation, sur l'antenne affichée ; vide, l'IA est refermée à tous ceux de
 * la formation qui l'avaient. Le quota reste celui de chaque compte : un
 * apprenant qui entre ensuite n'en a pas.
 */
export function ModaleQuotaFormation({
  formations,
  apprenants,
  site,
  reglage,
  onFermer,
}: {
  formations: LigneFormationIa[];
  apprenants: LigneApprenantIa[];
  site: string;
  reglage: ReglageIa;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const geste = useQuotaApprenantsFormation();
  const proposees = formations.filter((formation) => formation.active);
  // D'office, la première formation qui a des apprenants en formation sur l'antenne affichée.
  const [formationId, setFormationId] = useState(
    () =>
      proposees.find((formation) => apprenants.some((ligne) => ligne.enFormation && ligne.formation?.id === formation.id))
        ?.id ??
      proposees[0]?.id ??
      '',
  );
  const [retrait, setRetrait] = useState(false);
  const [quota, setQuota] = useState('');
  const concernes = apprenants.filter(
    (ligne) => ligne.formation?.id === formationId && (retrait ? ligne.quota !== null : ligne.enFormation && ligne.compteActif),
  ).length;

  const appliquer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    geste.mutate(
      { formationId, quotaJour: retrait ? null : lireSaisie(quota), ...(site ? { site } : {}) },
      {
        onSuccess: (resultat) => {
          toast(resultat.message);
          onFermer();
        },
      },
    );
  };

  return (
    <Modale
      titre="Quota IA des apprenants d’une formation"
      sousTitre="Le même quota pour chaque apprenant en formation, sur l’antenne affichée"
      ouverte
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={appliquer} className="space-y-4 p-4 sm:p-6">
        <label className="block min-w-0">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Formation</span>
          <select
            required
            value={formationId}
            onChange={(evenement) => setFormationId(evenement.target.value)}
            className={CLASSE_CHAMP_IA}
          >
            {proposees.map((formation) => (
              <option key={formation.id} value={formation.id}>
                {formation.titre}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="space-y-2">
          <legend className="mb-1 text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Geste</legend>
          {[
            { valeur: false, libelle: 'Accorder un quota aux apprenants en formation' },
            { valeur: true, libelle: 'Fermer l’IA à tous ceux de la formation qui l’ont' },
          ].map((option) => (
            <label key={option.libelle} className="flex min-h-[40px] items-center gap-2 text-sm text-codialis-navy">
              <input
                type="radio"
                name="geste-quota-formation"
                checked={retrait === option.valeur}
                onChange={() => setRetrait(option.valeur)}
                className="h-4 w-4 shrink-0 accent-codialis-blue"
              />
              {option.libelle}
            </label>
          ))}
        </fieldset>
        {!retrait && (
          <ChampNombre
            libelle="Quota"
            valeur={quota}
            onChange={setQuota}
            reglage={reglage}
            placeholder={`De ${reglage.min} à ${reglage.max}`}
            aide="Requêtes de tuteur par jour et par apprenant."
          />
        )}
        <p className="text-xs text-codialis-gray">
          {retrait
            ? `${concernes} apprenant${concernes > 1 ? 's' : ''} de cette formation ${concernes > 1 ? 'ont' : 'a'} un quota.`
            : `${concernes} apprenant${concernes > 1 ? 's' : ''} en formation avec un compte actif.`}{' '}
          Un apprenant qui entre ensuite n’a pas de quota : refaites le geste, ou réglez-le sur sa ligne.
        </p>
        {geste.error && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageErreur(geste.error, 'Le quota n’a pas pu être appliqué.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal
            type="submit"
            disabled={geste.isPending || !formationId || concernes === 0 || (!retrait && quota.trim() === '')}
          >
            {geste.isPending ? 'Enregistrement…' : retrait ? 'Fermer l’IA à ces apprenants' : 'Accorder ce quota'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
